import { AxiosError } from 'axios'
import type {
  APIErrorResponse,
  AuthError,
  ValidationError,
  ResourceError,
  TrainingError,
  PredictionError,
  DataSourceError,
  RateLimitError,
  PlanLimitError,
  InternalError,
} from '@/types/errors'
import {
  AuthErrorCode,
  ValidationErrorCode,
  ResourceErrorCode,
  TrainingErrorCode,
  PredictionErrorCode,
  DataSourceErrorCode,
  RateLimitErrorCode,
  PlanLimitErrorCode,
  InternalErrorCode,
} from '@/types/errors'

// Error category type (moved from types/errors.ts to ensure bundling)
export interface ErrorCategory {
  category: 'auth' | 'validation' | 'resource' | 'rate_limit' | 'server' | 'unknown'
  isRetryable: boolean
  requiresUserAction: boolean
}

// ============================================================================
// Type Guard Functions (moved from types/errors.ts for better bundling)
// ============================================================================

/**
 * Type guard for auth errors.
 * Safari was throwing "Can't find variable: isAuthError" when this was in types/errors.ts.
 */
export function isAuthError(error: APIErrorResponse): error is AuthError {
  return Object.values(AuthErrorCode).includes(error.error_code as AuthErrorCode)
}

export function isValidationError(error: APIErrorResponse): error is ValidationError {
  return Object.values(ValidationErrorCode).includes(error.error_code as ValidationErrorCode)
}

export function isResourceError(error: APIErrorResponse): error is ResourceError {
  return Object.values(ResourceErrorCode).includes(error.error_code as ResourceErrorCode)
}

export function isTrainingError(error: APIErrorResponse): error is TrainingError {
  return Object.values(TrainingErrorCode).includes(error.error_code as TrainingErrorCode)
}

export function isPredictionError(error: APIErrorResponse): error is PredictionError {
  return Object.values(PredictionErrorCode).includes(error.error_code as PredictionErrorCode)
}

export function isDataSourceError(error: APIErrorResponse): error is DataSourceError {
  return Object.values(DataSourceErrorCode).includes(error.error_code as DataSourceErrorCode)
}

export function isRateLimitError(error: APIErrorResponse): error is RateLimitError {
  return Object.values(RateLimitErrorCode).includes(error.error_code as RateLimitErrorCode)
}

export function isPlanLimitError(error: APIErrorResponse): error is PlanLimitError {
  return Object.values(PlanLimitErrorCode).includes(error.error_code as PlanLimitErrorCode)
}

export function isInternalError(error: APIErrorResponse): error is InternalError {
  return Object.values(InternalErrorCode).includes(error.error_code as InternalErrorCode)
}

/**
 * Categorize error based on HTTP status code.
 */
export function categorizeError(statusCode: number): ErrorCategory {
  if (statusCode === 401 || statusCode === 403) {
    return {
      category: 'auth',
      isRetryable: false,
      requiresUserAction: true,
    }
  }

  if (statusCode === 400 || statusCode === 422) {
    return {
      category: 'validation',
      isRetryable: false,
      requiresUserAction: true,
    }
  }

  if (statusCode === 404 || statusCode === 409 || statusCode === 410) {
    return {
      category: 'resource',
      isRetryable: false,
      requiresUserAction: true,
    }
  }

  if (statusCode === 429 || statusCode === 402) {
    return {
      category: 'rate_limit',
      isRetryable: true,
      requiresUserAction: false,
    }
  }

  if (statusCode >= 500) {
    return {
      category: 'server',
      isRetryable: true,
      requiresUserAction: false,
    }
  }

  return {
    category: 'unknown',
    isRetryable: false,
    requiresUserAction: true,
  }
}

/**
 * Type guard for Axios errors.
 */
export function isAxiosError(error: unknown): error is AxiosError {
  return error instanceof Error && 'isAxiosError' in error
}

/**
 * Type guard for API error responses.
 * Checks if error has the structure of an APIErrorResponse.
 */
export function isAPIErrorResponse(error: unknown): error is APIErrorResponse {
  return (
    typeof error === 'object' &&
    error !== null &&
    'error_code' in error &&
    'message' in error &&
    typeof (error as APIErrorResponse).error_code === 'string' &&
    typeof (error as APIErrorResponse).message === 'string'
  )
}

/**
 * Extract APIErrorResponse from any error type.
 * Handles Axios errors, APIErrorResponse objects, and generic errors.
 */
export function extractAPIError(error: unknown): APIErrorResponse | null {
  // Direct APIErrorResponse
  if (isAPIErrorResponse(error)) {
    return error
  }

  // Axios error with APIErrorResponse in response.data
  if (isAxiosError(error) && error.response?.data) {
    if (isAPIErrorResponse(error.response.data)) {
      return error.response.data
    }
  }

  return null
}

/**
 * Get user-friendly error message from any error type.
 * Prioritizes API error messages over generic error messages.
 */
export function getErrorMessage(error: unknown): string {
  // Try to extract structured API error first
  const apiError = extractAPIError(error)
  if (apiError) {
    return apiError.message
  }

  // Axios error (fallback for errors without structured response)
  if (isAxiosError(error)) {
    const data = error.response?.data as Record<string, unknown> | undefined
    return (data?.message as string) || error.message
  }

  // Standard Error
  if (error instanceof Error) {
    return error.message
  }

  // String error
  if (typeof error === 'string') {
    return error
  }

  return 'An unknown error occurred'
}

/**
 * Get API error message (legacy alias for getErrorMessage).
 * @deprecated Use getErrorMessage instead
 */
export function getApiErrorMessage(error: unknown): string {
  return getErrorMessage(error)
}

/**
 * Get error code from any error type.
 * Returns null if error doesn't have a code.
 */
export function getErrorCode(error: unknown): string | null {
  const apiError = extractAPIError(error)
  return apiError?.error_code || null
}

/**
 * Categorize error based on HTTP status code or error type.
 * Useful for determining retry behavior and user messaging.
 */
export function categorizeAPIError(error: unknown): ErrorCategory {
  // Try HTTP status code first (from Axios error)
  if (isAxiosError(error) && error.response?.status) {
    return categorizeError(error.response.status)
  }

  // Fallback: categorize by error code
  const apiError = extractAPIError(error)
  if (apiError) {
    if (isAuthError(apiError)) {
      return { category: 'auth', isRetryable: false, requiresUserAction: true }
    }
    if (isValidationError(apiError)) {
      return { category: 'validation', isRetryable: false, requiresUserAction: true }
    }
    if (isResourceError(apiError)) {
      return { category: 'resource', isRetryable: false, requiresUserAction: true }
    }
    if (isRateLimitError(apiError) || isPlanLimitError(apiError)) {
      return { category: 'rate_limit', isRetryable: true, requiresUserAction: false }
    }
    if (isInternalError(apiError)) {
      return { category: 'server', isRetryable: true, requiresUserAction: false }
    }
  }

  return { category: 'unknown', isRetryable: false, requiresUserAction: true }
}

/**
 * Check if error is retryable based on error type.
 * Rate limit errors and server errors are typically retryable.
 */
export function isRetryableError(error: unknown): boolean {
  return categorizeAPIError(error).isRetryable
}

/**
 * Check if error requires user action to resolve.
 * Auth, validation, and resource errors typically require user intervention.
 */
export function requiresUserAction(error: unknown): boolean {
  return categorizeAPIError(error).requiresUserAction
}

/**
 * Format error for logging/debugging.
 * Includes all available error details.
 */
export function formatErrorForLogging(error: unknown): Record<string, unknown> {
  const apiError = extractAPIError(error)
  const category = categorizeAPIError(error)

  return {
    message: getErrorMessage(error),
    error_code: apiError?.error_code,
    context: apiError?.context,
    category: category.category,
    isRetryable: category.isRetryable,
    requiresUserAction: category.requiresUserAction,
    httpStatus: isAxiosError(error) ? error.response?.status : undefined,
    // Include full error for debugging (in non-production)
    ...(import.meta.env.DEV && { rawError: error }),
  }
}
